/**
 * Save-play engine — spawn, simulate, commit, and atomically roll back the
 * governed save plays that savePlayTriggers.ts proposes.
 *
 * The lifecycle is deliberately staged so nothing ever fires-and-forgets:
 *
 *   spawnPlay    — compiles a SavePlaySpec into a ProposedPlay: the concrete
 *                  task graph (CSM-owned, real due dates), a tightened save
 *                  cadence, and a SEALED outcome contract (health_delta over
 *                  a window, real ARR at stake). Nothing is executed.
 *   simulatePlay — the blast-radius preview across N accounts BEFORE any
 *                  write: every change that WOULD happen, routed through the
 *                  SHIPPED policy simulator + blast-radius replay (injected —
 *                  never re-implemented), plus the affected-account roster.
 *                  A pure projection; inputs are never mutated.
 *   commitPlay   — folds one or many proposed plays into ONE batched,
 *                  approvable triage entry (never a per-account spam storm),
 *                  ranked by the shipped mission-queue fusion, and hands it
 *                  to the caller's approvalTriage seam exactly once.
 *   rollbackPlay — one atomic multi-account unwind: every spawned artifact
 *                  is reversibility-classified FIRST; if any artifact cannot
 *                  be undone the WHOLE rollback refuses (all-or-nothing),
 *                  otherwise every task across every account unwinds in one
 *                  gesture and the registry flips to 'rolled_back'.
 *
 * Deliberately PURE apart from the injected seams: no store, no network,
 * no implicit clock. The agent worker is an optional narrator at the UI
 * seam — the deterministic path always works without it.
 */

import type { SavePlaySpec, SavePlayStepSpec, TriggerAccount } from './savePlayTriggers';
import type {
  BuildContractOpts,
  ContractProposalInput,
  OutcomeContract,
} from '../agent/outcomeContract';
import type { AutonomyPolicy } from '../agent/autonomyPolicy';
import type { ScoredProposal, SimOutcome } from '../agent/policySimulator';
import type { PolicyDelta, TriageOutcome } from '../agent/policyBlastRadius';
import type { Reversibility } from '../agent/reversibility';
import type { MissionItem } from '../agent/missionQueue';

const DAY_MS = 86_400_000;

function usd(n: number): string {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

/** ISO date (YYYY-MM-DD) `days` after `now` (epoch ms). */
function isoAfter(now: number, days: number): string {
  return new Date(now + days * DAY_MS).toISOString().slice(0, 10);
}

// ---------------------------------------------------------------------------
// spawnPlay
// ---------------------------------------------------------------------------

/** One concrete, not-yet-created task of a proposed play. */
export interface PlannedTask {
  /** Deterministic: `${playId}:${stepKey}` — re-spawns never dupe. */
  id: string;
  stepKey: string;
  title: string;
  note: string;
  owner: string;
  /** ISO date (YYYY-MM-DD). */
  dueDate: string;
  accountId: string;
  accountName: string;
  /** Governed action class (reversibility-classified at rollback). */
  actionType: string;
  /** Real ARR the step chases — omitted when the account carries none. */
  value?: number;
}

/** Cadence rule shape the engine reads (crm/cadence.CadenceRule satisfies it). */
export interface CadenceRuleLike {
  checkInEveryDays: number;
  qbrEveryDays: number;
}

/** Injected deps for {@link spawnPlay} — the shipped pure builders. */
export interface SpawnPlayDeps {
  /** Optional extra/override step source; defaults to the spec's own steps. */
  playbook?: { steps?: SavePlayStepSpec[] };
  /** Cadence tiering (crm/cadence.cadenceTierFor + DEFAULT_CADENCE). */
  cadence: {
    tierFor: (arr: number | undefined) => string;
    rules: Record<string, CadenceRuleLike>;
  };
  /** Contract builder — the shipped agent/outcomeContract.buildOutcomeContract. */
  outcomeContract: {
    build: (proposal: ContractProposalInput, opts: BuildContractOpts) => OutcomeContract;
  };
}

/** The compiled, proposed (NOT executed) play. */
export interface ProposedPlay {
  id: string;
  spec: SavePlaySpec;
  owner: string;
  accountId: string;
  accountName: string;
  /** Ordered task graph — one planned task per spec step. */
  tasks: PlannedTask[];
  /** Save-mode cadence: the account's tier rule tightened (halved, floor 7d). */
  cadence: { tier: string; checkInEveryDays: number };
  /** The sealed success criterion the outcome verifier will replay. */
  contract: OutcomeContract;
  status: 'proposed';
  createdAt: number;
}

/**
 * Compile a play spec into a proposed play: task graph + cadence + sealed
 * outcome contract. Nothing is written — the play is data until a human
 * approves the committed batch. Pure and deterministic given `now`.
 */
export function spawnPlay(
  spec: SavePlaySpec,
  deps: SpawnPlayDeps,
  now: number = Date.now(),
): ProposedPlay {
  const steps = deps.playbook?.steps ?? spec.steps;
  const arr = spec.outcomeContract.dollarsAtStake;

  const tasks: PlannedTask[] = steps.map((step) => ({
    id: `${spec.id}:${step.key}`,
    stepKey: step.key,
    title: `${spec.account.name} · ${step.title}`,
    note: step.note,
    owner: spec.owner,
    dueDate: isoAfter(now, step.offsetDays),
    accountId: spec.account.id,
    accountName: spec.account.name,
    actionType: step.actionType,
    ...(arr > 0 ? { value: arr } : {}),
  }));

  // Save mode tightens the account's normal cadence: half the rhythm, floor 7d.
  const tier = deps.cadence.tierFor(spec.account.arr);
  const rule = deps.cadence.rules[tier];
  const checkInEveryDays = Math.max(7, Math.round((rule?.checkInEveryDays ?? 30) / 2));

  const contract = deps.outcomeContract.build(
    { id: spec.id, action: { kind: 'create_task', value: arr } },
    {
      kind: 'health_delta',
      now,
      windowMs: spec.outcomeContract.windowDays * DAY_MS,
      target: spec.outcomeContract.target,
      dollarsAtStake: arr,
    },
  );

  return {
    id: spec.id,
    spec,
    owner: spec.owner,
    accountId: spec.account.id,
    accountName: spec.account.name,
    tasks,
    cadence: { tier, checkInEveryDays },
    contract,
    status: 'proposed',
    createdAt: now,
  };
}

// ---------------------------------------------------------------------------
// simulatePlay
// ---------------------------------------------------------------------------

/** One write that WOULD happen if the play launches. Projection only. */
export interface PlannedChange {
  accountId: string;
  accountName: string;
  actionType: string;
  title: string;
  dueOffsetDays: number;
}

/** Injected deps for {@link simulatePlay} — the SHIPPED simulators. */
export interface SimulatePlayDeps {
  policySimulator: {
    /** The operator's live autonomy policy the writes would route through. */
    policy: AutonomyPolicy;
    /** agent/policySimulator.classifyProposal — the one true gate. */
    classify: (policy: AutonomyPolicy, proposal: ScoredProposal) => SimOutcome;
  };
  policyBlastRadius: {
    /** agent/policyBlastRadius.computeBlastRadius. */
    compute: (
      proposals: any[],
      oldPolicy: unknown,
      newPolicy: unknown,
      decide: (proposal: any, policy: any) => TriageOutcome,
    ) => PolicyDelta;
    /** agent/policyBlastRadius.blastRadiusSummary. */
    summarize: (delta: PolicyDelta) => string;
  };
}

/** The full pre-launch preview across N accounts. */
export interface PlaySimulation {
  changes: PlannedChange[];
  blastRadius: PolicyDelta & {
    /** Total writes the launch would perform. */
    writes: number;
    /** Distinct accounts the launch would touch. */
    accounts: number;
    /** Human one-liner for the confirm card. */
    summary: string;
  };
  affectedAccounts: { id: string; name: string; arrAtStake: number }[];
}

/**
 * Preview a play template across N accounts WITHOUT mutating anything:
 * every write that would happen, plus the blast radius computed by the
 * SHIPPED replay engine — "doing nothing" (everything held) vs the live
 * autonomy policy, so `nowAutoApprove` is exactly the set of writes that
 * would fire without a human if the play launches. Pure projection.
 */
export function simulatePlay(
  spec: SavePlaySpec,
  accounts: TriggerAccount[],
  deps: SimulatePlayDeps,
): PlaySimulation {
  const changes: PlannedChange[] = [];
  const proposals: (ScoredProposal & { proposalId: string })[] = [];

  for (const account of accounts) {
    for (const step of spec.steps) {
      const id = `${spec.id.replace(spec.account.id, account.id)}:${step.key}`;
      changes.push({
        accountId: account.id,
        accountName: account.name,
        actionType: step.actionType,
        title: `${account.name} · ${step.title}`,
        dueOffsetDays: step.offsetDays,
      });
      proposals.push({
        id,
        proposalId: id,
        agentId: 'save-play-engine',
        actionType: step.actionType,
        impact: account.arr ?? 0,
      });
    }
  }

  // Old policy = null sentinel: nothing runs today (every write held for a
  // human). New policy = the live autonomy policy. The delta's nowAutoApprove
  // is then precisely the writes that would auto-fire on launch.
  const decide = (proposal: any, policy: any): TriageOutcome =>
    policy == null
      ? 'require-approval'
      : deps.policySimulator.classify(policy, proposal) === 'auto'
        ? 'auto-approve'
        : 'require-approval';
  const delta = deps.policyBlastRadius.compute(
    proposals,
    null,
    deps.policySimulator.policy,
    decide,
  );

  return {
    changes,
    blastRadius: {
      ...delta,
      writes: changes.length,
      accounts: accounts.length,
      summary: deps.policyBlastRadius.summarize(delta),
    },
    affectedAccounts: accounts.map((a) => ({
      id: a.id,
      name: a.name,
      arrAtStake: a.arr ?? 0,
    })),
  };
}

// ---------------------------------------------------------------------------
// commitPlay
// ---------------------------------------------------------------------------

/** The single batched, approvable entry a commit produces. */
export interface BatchTriageEntry {
  /** Deterministic: `save-play-batch:${firstPlayId}`. */
  id: string;
  kind: 'save_play_batch';
  /** One headline for the whole batch — never one row per account. */
  title: string;
  playIds: string[];
  accountIds: string[];
  /** Total real ARR at stake across the batch. */
  dollarsAtStake: number;
  taskCount: number;
  /** Per-account cause lines — the click-verifiable "why". */
  evidence: string[];
  /** The fused, ranked mission card representing the batch. */
  mission: MissionItem;
}

/** One committed play inside the registry commitPlay returns. */
export interface CommittedPlay {
  play: ProposedPlay;
  batchId: string;
  status: 'committed' | 'rolled_back';
}

/** Injected deps for {@link commitPlay}. */
export interface CommitPlayDeps {
  /** agent/missionQueue.buildMissionQueue — dedupe + rank the batch cards. */
  missionQueue: { build: (items: MissionItem[]) => MissionItem[] };
  /**
   * The caller's approval seam: receives the ONE batched entry (e.g. queues
   * a governed approval into the Dashboard inbox). Called exactly once.
   */
  approvalTriage: { enqueue: (entry: BatchTriageEntry) => Promise<unknown> | unknown };
}

/**
 * Commit one or many proposed plays as ONE batched approvable entry — not a
 * per-account spam storm. The mission-queue fusion ranks the per-play cards
 * so the batch headline leads with the highest-stakes account; the caller's
 * approvalTriage seam is invoked exactly once with the batch. Async because
 * the seam usually persists (queueApproval).
 */
export async function commitPlay(
  play: ProposedPlay | ProposedPlay[],
  deps: CommitPlayDeps,
): Promise<{ entry: BatchTriageEntry; plays: CommittedPlay[] }> {
  const plays = Array.isArray(play) ? play : [play];
  if (plays.length === 0) throw new Error('commitPlay: no plays to commit');

  const items: MissionItem[] = plays.map((p) => ({
    id: p.id,
    sourceAgent: 'save-play-engine',
    accountId: p.accountId,
    title: `${p.spec.label} — ${p.accountName}`,
    dollarsAtStake: p.contract.dollarsAtStake,
    urgency: p.spec.severity,
    confidence: 0.8,
    autonomyTier: 1,
    proposalRef: p.id,
    evidence: p.spec.causes.map((c) => `${p.accountName}: ${c}`),
  }));
  const ranked = deps.missionQueue.build(items);
  const lead = ranked[0];

  const batchId = `save-play-batch:${plays[0].id}`;
  const dollarsAtStake = plays.reduce((sum, p) => sum + p.contract.dollarsAtStake, 0);
  const taskCount = plays.reduce((sum, p) => sum + p.tasks.length, 0);

  const entry: BatchTriageEntry = {
    id: batchId,
    kind: 'save_play_batch',
    title:
      plays.length === 1
        ? `Launch ${plays[0].spec.label} — ${plays[0].accountName} (${usd(dollarsAtStake)} ARR at stake)`
        : `Launch ${plays.length} save plays (lead: ${lead.title}) — ${usd(dollarsAtStake)} ARR at stake`,
    playIds: plays.map((p) => p.id),
    accountIds: plays.map((p) => p.accountId),
    dollarsAtStake,
    taskCount,
    evidence: ranked.flatMap((i) => i.evidence),
    mission: lead,
  };

  await deps.approvalTriage.enqueue(entry); // exactly once — the batch IS the unit

  return {
    entry,
    plays: plays.map((p) => ({ play: p, batchId, status: 'committed' as const })),
  };
}

// ---------------------------------------------------------------------------
// rollbackPlay
// ---------------------------------------------------------------------------

/** Injected deps + registry for {@link rollbackPlay}. */
export interface RollbackPlayDeps {
  /** agent/reversibility.classifyReversibility — the fail-safe classifier. */
  reversibility: { classify: (actionType: string) => Reversibility };
  /** The committed-play registry to unwind from. */
  plays: CommittedPlay[];
  /** Optional side-effect seam invoked per undone artifact (delete task,
   * dismiss pending approval…). Only called when the atomic check passes. */
  undo?: (task: PlannedTask) => void;
}

export interface RollbackResult {
  playId: string;
  /** True only when EVERY artifact unwound — all-or-nothing. */
  rolledBack: boolean;
  /** Every artifact undone (empty when the rollback refused). */
  undone: PlannedTask[];
  /** Artifacts that block the rollback (irreversible classes). */
  blocked: { taskId: string; actionType: string; reason: string }[];
  /** New registry with matched plays flipped to 'rolled_back' on success. */
  plays: CommittedPlay[];
}

/**
 * One atomic multi-account unwind. `playId` matches either a single play id
 * or a batch id (unwinding EVERY play committed in that batch). Every
 * artifact is reversibility-classified FIRST; if any classifies
 * 'irreversible' the WHOLE rollback refuses and nothing is undone —
 * all-or-nothing, never a partial unwind. On success the undo seam runs for
 * every artifact and the returned registry flips the plays to
 * 'rolled_back'. The input registry is never mutated.
 */
export function rollbackPlay(playId: string, deps: RollbackPlayDeps): RollbackResult {
  const matched = deps.plays.filter(
    (c) => c.status === 'committed' && (c.play.id === playId || c.batchId === playId),
  );
  const artifacts = matched.flatMap((c) => c.play.tasks);

  // Atomic gate: classify EVERYTHING before undoing ANYTHING.
  const blocked = artifacts
    .filter((t) => deps.reversibility.classify(t.actionType) === 'irreversible')
    .map((t) => ({
      taskId: t.id,
      actionType: t.actionType,
      reason: `'${t.actionType}' cannot be undone — rollback refused for the whole play`,
    }));

  if (matched.length === 0 || blocked.length > 0) {
    return { playId, rolledBack: false, undone: [], blocked, plays: [...deps.plays] };
  }

  for (const task of artifacts) deps.undo?.(task);

  const matchedIds = new Set(matched.map((c) => c.play.id));
  return {
    playId,
    rolledBack: true,
    undone: artifacts,
    blocked: [],
    plays: deps.plays.map((c) =>
      matchedIds.has(c.play.id) ? { ...c, status: 'rolled_back' as const } : c,
    ),
  };
}
